"use client";

import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useState, useRef } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { toast } from "sonner";

import { SplitHandle, splitPaneVars } from "@demo/components/ResizableSplit";
import AgentOutput from "@demo/features/agent-output";
import DocumentEditor from "@demo/features/document-editor";
import { EditorDropZone } from "@demo/features/drop-zone";
import ExamplesPanel from "@demo/features/examples";
import InlineExamplesPreview from "@demo/features/examples/InlinePreview";
import { useHasRunOutput } from "@demo/hooks/useHasRunOutput";
import { analytics, ANALYTICS_EVENTS } from "@demo/libs/analytics";
import { sendToModel } from "@demo/libs/model";
import { PANEL_DEFAULTS, PANEL_MINIMUMS } from "@demo/libs/panel-layout";
import { DocumentEditorRef, useEditorRefStore } from "@demo/store/editor-ref";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";
import { useIsAnyFileUploading } from "@demo/store/upload-progress";
import { Role } from "@demo/types/base";
import { clearDocument } from "@demo/utils/document";
import { getEditorErrorMessage } from "@demo/utils/strings";

import { useActiveTab } from "./ActiveTabContext";
import ComposeFooter from "./ComposeFooter";

// Matches the `duration-300` on ExamplesPanel's opacity/transform transition;
// keep these in sync so the panel finishes its exit animation before unmount.
const EXAMPLES_PANEL_EXIT_MS = 300;

/**
 * Returns true while the panel should remain in the DOM. Flips to false
 * `durationMs` after `isOpen` goes false, so the panel's exit transition
 * has time to play before React removes it.
 */
function useDeferredUnmount(isOpen: boolean, durationMs: number): boolean {
  const [shouldRender, setShouldRender] = useState(isOpen);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      return;
    }
    const timer = setTimeout(() => setShouldRender(false), durationMs);
    return () => clearTimeout(timer);
  }, [isOpen, durationMs]);
  return shouldRender;
}

const ROW_GAP = 16;
const PROMPT_SELECTION_DRAG_THRESHOLD = 5;

function focusEditorAtPoint(editor: NonNullable<DocumentEditorRef["editor"]>, clientX: number, clientY: number) {
  if (editor.isDestroyed) return;

  const bounds = editor.view.dom.getBoundingClientRect();
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, Math.max(min, max - 1)));
  const position = editor.view.posAtCoords({
    left: clamp(clientX, bounds.left, bounds.right),
    top: clamp(clientY, bounds.top, bounds.bottom),
  });

  if (position) editor.chain().focus(position.pos).run();
}

/**
 * Compose's input pane: the rich-text prompt editor (with its drop-zone,
 * click/touch-to-focus, and inline examples) plus Compose's input orchestration —
 * content/readiness state, the run/clear actions, the Cmd+Enter shortcut, the
 * inline agent output, and the footer. It composes the standalone editor / examples
 * / drop-zone features; another mode would assemble those same features into its own pane.
 */
const ComposeInput = () => {
  const setEditorRef = useEditorRefStore((state) => state.setEditorRef);

  const agentState = useModelStore((state) => state.agentState);
  const isAnyMediaUploading = useIsAnyFileUploading();
  const { setActiveTab } = useActiveTab();

  const [isDragging, setIsDragging] = useState(false);
  const [isExamplesPanelOpen, setIsExamplesPanelOpen] = useState(false);
  const isExamplesPanelMounted = useDeferredUnmount(isExamplesPanelOpen, EXAMPLES_PANEL_EXIT_MS);

  const [hasUserContent, setHasUserContent] = useState<{ text: boolean; media: boolean }>({
    text: false,
    media: false,
  });
  const promptPointerGesture = useRef<{ startX: number; startY: number; moved: boolean } | null>(null);

  const handleUserPromptFileDrop = useCallback((files: File[]) => {
    const userEditorRef = useEditorRefStore.getState().userEditorRef;
    if (userEditorRef?.handleFileDrop) {
      userEditorRef.handleFileDrop(files);
    }
  }, []);

  const handleUserPromptClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const wasDrag = promptPointerGesture.current?.moved ?? false;
    promptPointerGesture.current = null;
    if (wasDrag) return;

    const target = e.target as Element;
    if (target.closest(".ProseMirror, button, input, select, textarea, a, [role='button']")) return;

    const { clientX, clientY } = e;
    requestAnimationFrame(() => {
      const editor = useEditorRefStore.getState().userEditorRef?.getEditor();
      if (editor) focusEditorAtPoint(editor, clientX, clientY);
    });
  }, []);

  const handlePromptPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    promptPointerGesture.current =
      event.isPrimary && event.button === 0 && event.pointerType !== "touch"
        ? { startX: event.clientX, startY: event.clientY, moved: false }
        : null;
  }, []);

  const handlePromptPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = promptPointerGesture.current;
    if (!gesture || gesture.moved) return;

    gesture.moved =
      Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY) >= PROMPT_SELECTION_DRAG_THRESHOLD;
  }, []);

  const handlePromptPointerCancel = useCallback(() => {
    promptPointerGesture.current = null;
  }, []);

  const handleUserEditorRef = useCallback(
    (instance: DocumentEditorRef | null) => {
      setEditorRef(Role.USER, instance);
    },
    [setEditorRef],
  );

  const isReadyToRun = useMemo(() => {
    return !!hasUserContent.text && !!hasUserContent.media && !isAnyMediaUploading && agentState === AgentState.IDLE;
  }, [agentState, hasUserContent, isAnyMediaUploading]);

  const hasAnyContent = useMemo(() => hasUserContent.text || hasUserContent.media, [hasUserContent]);

  const handleSendToModel = useCallback(async () => {
    const currentAgentState = useModelStore.getState().agentState;

    if (currentAgentState !== AgentState.IDLE) {
      return;
    }

    if (!isReadyToRun) {
      if (!hasUserContent.media) {
        toast.error(getEditorErrorMessage("noMedia"));
      } else if (!hasUserContent.text) {
        toast.error(getEditorErrorMessage("noContent"));
      } else if (isAnyMediaUploading) {
        toast.error(getEditorErrorMessage("mediaUploading"));
      } else {
        toast.error(getEditorErrorMessage("genericError"));
      }

      return;
    }

    // sendToModel reads the request content synchronously from the editor
    // itself (editor.getJSON()), so every keystroke up to this point is
    // already included — no flush step is needed.
    setActiveTab("output");
    await sendToModel();
  }, [isReadyToRun, hasUserContent, isAnyMediaUploading, setActiveTab]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleSendToModel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [handleSendToModel]);

  const closeExamplesPanel = useCallback((source: "close_button" | "template_selected" | "user_typed") => {
    setIsExamplesPanelOpen((wasOpen) => {
      if (wasOpen) analytics.track(ANALYTICS_EVENTS.EXAMPLES_PANEL_CLOSED, { source });
      return false;
    });
  }, []);

  const handleTemplateSelect = useCallback(() => {
    setHasUserContent({ text: true, media: true });
    closeExamplesPanel("template_selected");
  }, [closeExamplesPanel]);

  const handleOpenExamplesPanel = useCallback(() => {
    setIsExamplesPanelOpen(true);
    analytics.track(ANALYTICS_EVENTS.EXAMPLES_PANEL_OPENED, { source: "see_all_button" });
  }, []);

  const handleCloseExamplesPanel = useCallback(() => {
    closeExamplesPanel("close_button");
  }, [closeExamplesPanel]);

  const onClearDocument = useCallback(() => {
    clearDocument();
    setHasUserContent({ text: false, media: false });
  }, []);

  useEffect(() => {
    if (hasUserContent.text || hasUserContent.media) {
      closeExamplesPanel("user_typed");
    }
  }, [hasUserContent.text, hasUserContent.media, closeExamplesPanel]);

  const images = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const hasRunOutput = useHasRunOutput();
  const isEmptyResponse = useModelStore((state) => state.isEmptyResponse);

  const rowSplitRef = useRef<HTMLDivElement>(null);
  const [rowFraction, setRowFraction] = useState<number>(PANEL_DEFAULTS.vertical);

  const displayAgentOutput = useMemo(() => {
    // The whole run, not just evaluating: the first token resets the output and moves to streaming a frame
    // before its text commits, and hiding for that frame restarts the reveal.
    const isRunning = agentState === AgentState.EVALUATING || agentState === AgentState.STREAMING;

    return !!images.length && (isRunning || hasRunOutput || isEmptyResponse);
  }, [agentState, hasRunOutput, isEmptyResponse, images.length]);

  return (
    <div className="flex h-full w-full flex-col gap-4">
      {/* Only the two panes, so the clamp's 100% is exactly them and the gap
          between them — the footer sits outside and is not its to budget. */}
      <div
        className="relative flex min-h-0 flex-1 flex-col gap-4"
        ref={rowSplitRef}
        style={splitPaneVars(rowFraction, PANEL_MINIMUMS.editor, PANEL_MINIMUMS.agentOutput, ROW_GAP)}
      >
        <EditorDropZone
          className={cn(
            "split-pane-vertical relative min-h-0 flex-1 cursor-text overflow-y-auto overflow-x-hidden rounded-2xl border border-background-alt bg-background-alt-2 shadow-none [scrollbar-gutter:stable] dark:border-border sm:shadow-big",
            "transition-[height] duration-500 ease-in-out motion-reduce:transition-none",
          )}
          // Full height until there is output, then it eases down to the split
          // while the response pane below grows up into the freed space.
          data-drop-zone="user-prompt-container"
          data-testid="user-prompt-container"
          style={displayAgentOutput ? undefined : ({ "--split-size": "100%" } as React.CSSProperties)}
          onClick={handleUserPromptClick}
          onFilesDropped={handleUserPromptFileDrop}
        >
          <div
            className="relative flex h-full flex-col overflow-x-hidden p-3 pb-14 sm:pb-3"
            onPointerCancel={handlePromptPointerCancel}
            onPointerDown={handlePromptPointerDown}
            onPointerMove={handlePromptPointerMove}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (isDragging) {
                setIsDragging(false);
                return;
              }

              // Read at event time — the ref registers after this component's
              // first render, so a render-time snapshot could be stale/null.
              const editor = useEditorRefStore.getState().userEditorRef?.getEditor();
              if (!editor) {
                return;
              }

              const touch = e.changedTouches[0];

              if (!touch) {
                return;
              }

              focusEditorAtPoint(editor, touch.clientX, touch.clientY);
            }}
            onTouchMove={() => {
              if (!isDragging) {
                setIsDragging(true);
              }
            }}
          >
            <DocumentEditor
              id="user-prompt"
              ref={handleUserEditorRef}
              type={Role.USER}
              onHasEditorContent={setHasUserContent}
            />
          </div>

          <InlineExamplesPreview
            isVisible={!hasAnyContent && !isExamplesPanelOpen}
            onOpenExamplesPanel={handleOpenExamplesPanel}
            onTemplateSelect={handleTemplateSelect}
          />
          {isExamplesPanelMounted && (
            <ExamplesPanel
              isVisible={isExamplesPanelOpen && !hasAnyContent}
              onClose={handleCloseExamplesPanel}
              onTemplateSelect={handleTemplateSelect}
            />
          )}
        </EditorDropZone>

        {displayAgentOutput && (
          <SplitHandle
            containerRef={rowSplitRef}
            fraction={rowFraction}
            gap={ROW_GAP}
            label={strings.compose.resizeRows}
            minFirst={PANEL_MINIMUMS.editor}
            minSecond={PANEL_MINIMUMS.agentOutput}
            orientation="vertical"
            onFractionChange={setRowFraction}
          />
        )}

        <div
          className={cn(
            "hidden min-h-0 flex-1 sm:block",
            "transition-[margin,opacity] duration-500 ease-in-out motion-reduce:transition-none",
            displayAgentOutput ? "mt-0 opacity-100" : "pointer-events-none -mt-4 opacity-0",
          )}
        >
          <AgentOutput />
        </div>
      </div>

      <ComposeFooter
        hasAnyContent={hasAnyContent}
        isReadyToRun={isReadyToRun}
        onClearDocument={onClearDocument}
        onSubmit={handleSendToModel}
      />
    </div>
  );
};

export default ComposeInput;
